"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/feedback";
import {
  Network,
  Truck,
  Sparkles,
  TrendingUp,
  Landmark,
  Users,
  ArrowRight,
  Bot,
  Activity,
  AlertTriangle,
  UserCheck,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  fetchTeamsAction,
  updateTeamAction,
  deleteTeamAction,
  fetchPeopleAction,
} from "@/actions/people-actions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/overlays";
import { Label, Select, Input } from "@/components/ui/form-elements";

// Helper to resolve Team Icons
function TeamIcon({ name, className }: { name: string; className?: string }) {
  switch (name) {
    case "Truck":
      return <Truck className={className} />;
    case "Sparkles":
      return <Sparkles className={className} />;
    case "Network":
      return <Network className={className} />;
    case "TrendingUp":
      return <TrendingUp className={className} />;
    case "Landmark":
      return <Landmark className={className} />;
    default:
      return <Users className={className} />;
  }
}

export default function TeamsDirectoryPage() {
  const [teams, setTeams] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editManagerId, setEditManagerId] = useState("");
  const [editName, setEditName] = useState("");
  const [editMission, setEditMission] = useState("");
  const [editIconName, setEditIconName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState<"all" | "overloaded">("all");

  const loadTeams = async () => {
    setLoading(true);
    try {
      const data = await fetchTeamsAction();
      setTeams(data);
      const peopleData = await fetchPeopleAction();
      setPeople(peopleData);
    } catch (err) {
      console.error("Error loading teams:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  const handleOpenEdit = (team: any) => {
    setSelectedTeam(team);
    setEditName(team.name);
    setEditMission(team.mission || "");
    setEditIconName(team.iconName || "Users");
    setEditManagerId(team.manager?.id || "");
    setIsEditOpen(true);
  };

  const handleUpdateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam) return;

    setActionLoading(true);
    try {
      const res = await updateTeamAction(selectedTeam.name, {
        name: editName,
        mission: editMission,
        iconName: editIconName,
        managerId: editManagerId,
      });
      if (res.error) {
        alert(res.error);
      } else {
        setIsEditOpen(false);
        setSelectedTeam(null);
        await loadTeams();
      }
    } catch (err) {
      console.error("Error updating team:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTeam = async (e: React.MouseEvent, teamName: string) => {
    e.stopPropagation();
    if (
      !confirm(
        `Sei sicuro di voler sciogliere il team "${teamName}"? Tutti i membri saranno impostati su Unassigned.`,
      )
    )
      return;

    setActionLoading(true);
    try {
      await deleteTeamAction(teamName);
      await loadTeams();
    } catch (err) {
      console.error("Error deleting team:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Compute aggregate metrics
  const totalTeams = teams.length;
  const totalMembers = teams.reduce((acc, t) => acc + t.membersCount, 0);
  const avgWorkload =
    totalTeams > 0
      ? Math.round(
          teams.reduce((acc, t) => acc + t.averageCapacity, 0) / totalTeams,
        )
      : 0;
  const criticalTeamsCount = teams.filter((t) => t.averageCapacity > 80).length;

  const filteredTeams = teams.filter((t) => {
    if (teamFilter === "overloaded") return t.averageCapacity > 80;
    return true;
  });

  return (
    <div className="h-full overflow-y-auto px-8 py-8 scrollbar-thin">
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text-primary flex items-center gap-2">
              <Network className="h-6 w-6 text-primary-600" />
              Struttura Team
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              Visualizza l'organizzazione dei dipartimenti, monitora la capacità
              media e alloca le risorse.
            </p>
          </div>
        </div>

        {/* Aggregate KPI Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card
            onClick={() => setTeamFilter("all")}
            className={`flex flex-col justify-between py-2 cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
              teamFilter === "all"
                ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Team Attivi
              </span>
              <Network className="h-4 w-4 text-text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-text-primary">
                {loading ? "..." : totalTeams}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => setTeamFilter("all")}
            className={`flex flex-col justify-between py-2 cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
              teamFilter === "all"
                ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-medium">
                Totale Risorse
              </span>
              <Users className="h-4 w-4 text-text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-text-primary">
                {loading ? "..." : totalMembers}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => setTeamFilter("all")}
            className={`flex flex-col justify-between py-2 cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
              teamFilter === "all"
                ? "ring-2 ring-primary-600 border-primary-500 bg-primary-500/[0.01]"
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Saturazione Media
              </span>
              <Activity className="h-4 w-4 text-text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-text-primary">
                {loading ? "..." : `${avgWorkload}%`}
              </div>
            </CardContent>
          </Card>

          <Card
            onClick={() => setTeamFilter("overloaded")}
            className={`flex flex-col justify-between py-2 cursor-pointer select-none transition-all hover:scale-[1.01] hover:shadow-sm ${
              teamFilter === "overloaded"
                ? "ring-2 ring-error-600 border-error-500 bg-error-500/[0.01]"
                : ""
            }`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Team in Sovraccarico
              </span>
              <AlertTriangle className="h-4 w-4 text-error" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-error flex items-baseline gap-2">
                {loading ? "..." : criticalTeamsCount}
                {criticalTeamsCount > 0 && (
                  <span className="text-xs font-medium text-text-secondary">
                    richiede attenzione
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Directory Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="h-5 bg-border-strong/20 rounded w-1/3" />
                    <div className="h-5 bg-border-strong/20 rounded w-16" />
                  </div>
                  <div className="h-3 bg-border-strong/20 rounded w-5/6" />
                  <div className="h-10 bg-border-strong/20 rounded w-full" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredTeams.map((team) => {
              const isOverloaded = team.averageCapacity > 80;
              const isCritical = team.averageCapacity > 90;

              return (
                <Card
                  key={team.id}
                  onClick={() => handleOpenEdit(team)}
                  className="flex flex-col justify-between h-full group hover:shadow-md transition-shadow cursor-pointer select-none"
                >
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary-50 text-primary-600 border border-primary-100 flex items-center justify-center shrink-0 shadow-xs">
                          <TeamIcon name={team.iconName} className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-text-primary text-lg hover:text-primary-600 transition-colors">
                            <Link
                              href={`/teams/${team.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {team.name}
                            </Link>
                          </h3>
                          <p className="text-xs text-text-secondary mt-0.5">
                            {team.membersCount} membri attivi
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link
                            href={`/explore?nodeId=${team.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Vedi nel Grafo"
                          >
                            <Network className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(team);
                            }}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                            title="Modifica"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteTeam(e, team.name)}
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <Badge
                          variant={
                            isCritical
                              ? "error"
                              : isOverloaded
                                ? "warning"
                                : "success"
                          }
                        >
                          {isCritical
                            ? "Critical workload"
                            : isOverloaded
                              ? "High workload"
                              : "Healthy capacity"}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-6 py-0 flex-1 space-y-5">
                    {/* Mission statement */}
                    <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
                      {team.mission}
                    </p>

                    {/* Capacity average meter */}
                    <div className="space-y-1.5 bg-card-muted/30 border border-border/40 p-3 rounded-xl">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-text-secondary font-medium flex items-center gap-1">
                          <UserCheck className="h-3.5 w-3.5 text-text-muted" />
                          Saturazione Media del Team:
                        </span>
                        <span
                          className={`font-bold ${
                            isCritical
                              ? "text-error"
                              : isOverloaded
                                ? "text-warning"
                                : "text-success"
                          }`}
                        >
                          {team.averageCapacity}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-border-strong/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            isCritical
                              ? "bg-error"
                              : isOverloaded
                                ? "bg-warning"
                                : "bg-success"
                          }`}
                          style={{ width: `${team.averageCapacity}%` }}
                        />
                      </div>
                    </div>

                    {/* Manager Overview & Avatars */}
                    <div className="flex items-center justify-between border-t border-border/40 pt-4">
                      {team.manager ? (
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-primary-600 text-text-on-primary flex items-center justify-center font-bold text-[10px] select-none shadow-xs">
                            {team.manager.avatar}
                          </div>
                          <div className="text-[10px] leading-tight">
                            <p className="font-semibold text-text-primary text-xs">
                              {team.manager.name}
                            </p>
                            <p className="text-text-muted">Team Lead</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] font-medium text-text-muted">
                          Nessun lead assegnato
                        </span>
                      )}

                      {/* Avatars group */}
                      <div className="flex -space-x-2 overflow-hidden">
                        {team.members.slice(0, 4).map((member: any) => (
                          <div
                            key={member.id}
                            className={`inline-block h-6 w-6 rounded-full border-2 border-card flex items-center justify-center font-bold text-[9px] select-none ${
                              member.type === "AI"
                                ? "bg-category-innovation-soft text-category-innovation border-category-innovation-border"
                                : "bg-card-muted text-text-secondary border-border"
                            }`}
                            title={member.name}
                          >
                            {member.avatar}
                          </div>
                        ))}
                        {team.members.length > 4 && (
                          <div className="inline-block h-6 w-6 rounded-full bg-card-muted border-2 border-card flex items-center justify-center text-[9px] font-semibold text-text-secondary">
                            +{team.members.length - 4}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="p-6 pt-4 border-t border-border/40 mt-4 bg-card-muted/50 rounded-b-xl flex justify-end">
                    <Link
                      href={`/teams/${team.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 px-3 h-8 text-xs hover:bg-card"
                      >
                        Dettaglio Team
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifica Team: {selectedTeam?.name}</DialogTitle>
              <DialogDescription>
                Assegna un nuovo manager per questo team.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpdateTeam} className="space-y-4 my-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-team-name">Nome Team</Label>
                <Input
                  id="edit-team-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-team-mission">
                  Missione / Descrizione
                </Label>
                <Input
                  id="edit-team-mission"
                  value={editMission}
                  onChange={(e) => setEditMission(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-team-icon">Icona del Team</Label>
                <Select
                  id="edit-team-icon"
                  value={editIconName}
                  onChange={(e) => setEditIconName(e.target.value)}
                >
                  <option value="Users">Default (Users)</option>
                  <option value="Truck">Logistica (Truck)</option>
                  <option value="Sparkles">Design/Prodotto (Sparkles)</option>
                  <option value="Network">Core Tech (Network)</option>
                  <option value="TrendingUp">
                    Sales/Crescita (TrendingUp)
                  </option>
                  <option value="Landmark">Amministrazione (Landmark)</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-team-manager">Manager del Team</Label>
                <Select
                  id="edit-team-manager"
                  value={editManagerId}
                  onChange={(e) => setEditManagerId(e.target.value)}
                >
                  <option value="">Nessun Manager</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.type === "AI" ? "AI" : "Human"})
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <DialogClose asChild>
                  <Button variant="secondary" type="button">
                    Annulla
                  </Button>
                </DialogClose>
                <Button type="submit" loading={actionLoading}>
                  Salva Modifiche
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
